import { ensureArray } from '@mbrtargeting/metatag-utils';
import { MetaTag } from '../../../loader/essentials/init-metatag.js';
import { log } from '../../../loader/essentials/logger.js';
import { Matcher, waitForPostMessage } from '../../../utils/wait-for-postmessage.js';
import { setIdsForAdSlot } from '../../utils/adslot-data-store.js';

declare const window: Window & {
    SDG?: MetaTag;
};

const SDG_START_RENDER: Matcher<{ slotName: string, data: { viewabilityId?: string, formatName?: string } }> = {
    regex: /^SDG_START_RENDER#([a-zA-Z0-9_-]+)(#(.*))?/,
    extract: (match) => ({
        slotName: match[1],
        data: match[3] ? JSON.parse(match[3]) : {},
    }),
};

const SDG_AD_DELIVERED: Matcher<{ slotName: string, width: number, height: number }> = {
    regex: /^SDG_AD_DELIVERED#([a-zA-Z0-9_-]+)#size([0-9]+)x([0-9]+)/,
    extract: (match) => ({
        slotName: match[1],
        width: parseInt(match[2]),
        height: parseInt(match[3])
    })
};

const SDG_FINISH_RENDER: Matcher<{ slotName: string }> = {
    regex: /^SDG_FINISH_RENDER#([a-zA-Z0-9_-]+)/,
    extract: (match) => ({
        slotName: match[1],
    }),
};

const SDG_MEASURE_ELEMENT: Matcher<{ slotName: string, visibilityNodes: Array<{ node: string, name: string }> }> = {
    regex: /^SDG_MEASURE_ELEMENT#([a-zA-Z0-9_-]+)#(.*)/,
    extract: (match) => ({
        slotName: match[1],
        visibilityNodes: JSON.parse(match[2]),
    }),
};

waitForPostMessage(SDG_START_RENDER, ({ slotName, data }) => {
    const adslot = window.SDG?.Advertising?.getSlotByName(slotName);
    if (!adslot) return log.error('SYSTEM: can not found slotName "%s" reported by postMessage SDG_START_RENDER', [slotName.toUpperCase()]);

    log.debug('%s: postMessage SDG_START_RENDER received %o', [slotName.toUpperCase(), data]);
    const { formatName, viewabilityId = formatName } = data; // if viewabilityId is not given, use formatName as viewabilityId
    if (viewabilityId) setIdsForAdSlot(slotName, { source: 'postmessage', values: { viewabilityId } });

    // start render process, do not await
    adslot.renderCreative(new Promise<void>(resolve => {
        const ac = new AbortController();

        // when postMessage "SDG_MEASURE_ELEMENT" is received, add visibility nodes to the adslot
        waitForPostMessage(SDG_MEASURE_ELEMENT, ({ slotName, visibilityNodes }) => {
            if (adslot.slotName !== slotName) return; //make sure postMessage fits the slot used before
            log.debug('%s: postMessage SDG_MEASURE_ELEMENT received: %o', [slotName.toUpperCase(), visibilityNodes]);
            for (const { node, name } of ensureArray(visibilityNodes)) {
                adslot.addVisibilityNode({ name, node });
            }
        }, { once: false, signal: ac.signal });

        // when postMessage "SDG_FINISH_RENDER" is received, consider rendering to be complete & start viewability measurement.
        waitForPostMessage(SDG_FINISH_RENDER, ({ slotName }) => {
            if (adslot.slotName !== slotName) return; //make sure postMessage fits the slot used before
            log.debug('%s: postMessage SDG_FINISH_RENDER received', [slotName.toUpperCase()]);
            ac.abort();
        }, { once: false, signal: ac.signal });

        // if finish message is not received in 25 secs, assume rendering is done
        const timer = window.setTimeout(() => {
            log.error('%s: postMessage SDG_FINISH_RENDER missing (timeout). Advancing without creative being fully rendered.', [slotName.toUpperCase()]);
            ac.abort();
        }, 25000);

        ac.signal.addEventListener('abort', () => {
            window.clearTimeout(timer);
            resolve();
        }, { once: true });

    }));
}, { once: false });

/**
 * Used by 3rd parties to let us know what dimensions their creative will use.
 * Will help with CLS avoidance and makes sure adslots are large enough to display this ad.
 */
waitForPostMessage(SDG_AD_DELIVERED, ({ slotName, width, height }) => {
    log.debug('%s: postMessage SDG_AD_DELIVERED received: %dx%d', [slotName.toUpperCase(), width, height]);
    window.SDG?.Advertising?.getSlotByName(slotName)?.reportAdDelivery(width, height, false);
}, { once: false });

export class PromiseQueue {
    /** store promises in a Set to ensure uniqueness and efficient deletion */
    #promises = new Set<Promise<any>>();

    #onStartCallbacks = new Set<() => void>();
    #onEndCallbacks = new Set<() => void>();

    /** timer for delaying execution of end callbacks after the last promise is completed */
    #timer = -1;

    /**
     * Initializes a new instance of the PromiseQueue class.
     *
     * @param closingDelay The delay (in milliseconds) before executing end callbacks after all promises are completed.
     */
    constructor(
        private readonly closingDelay: number = 25,
    ) { }

    /**
     * Adds a promise to the queue and manages its lifecycle.
     *
     * @param promise The promise to add to the queue.
     */
    public add<Type>(promise: Promise<Type>) {
        // if this is the first promise, trigger start callbacks
        if (this.#promises.size === 0) this.#onStartCallbacks.forEach(callback => callback());

        // new command received, halt timer if running
        window.clearTimeout(this.#timer);

        // add promise to collection
        this.#promises.add(promise);

        // when promise is resolved or rejected, update internal data
        promise.finally(() => {
            // remove promise from collection
            this.#promises.delete(promise);

            // if last one done, timer will wait shortly for new commands before end
            if (this.#promises.size === 0) {
                // cancel previous timer if running
                window.clearTimeout(this.#timer);
                // setup new timer to trigger end callbacks
                this.#timer = window.setTimeout(() => this.#onEndCallbacks.forEach(callback => callback()), this.closingDelay);
            }
        });
        return promise;
    }

    /**
     * Adds a callback function to execute when promises start.
     *
     * @param callback The callback function to execute when promises start.
     * @returns The PromiseQueue instance for chaining.
     */
    public onStart(callback: () => void) {
        this.#onStartCallbacks.add(callback);
        return this;
    }

    /**
     * Adds a callback function to execute when all promises are completed.
     *
     * @param callback The callback function to execute when all promises are completed.
     * @returns The PromiseQueue instance for chaining.
     */
    public onEnd(callback: () => void) {
        this.#onEndCallbacks.add(callback);
        return this;
    }
}
